import React, { useState } from 'react';
import { useContacts } from '../hooks/useContacts';
import { useKeyboardHeight } from '../hooks/useKeyboardHeight';
import { ManualEntryForm } from './ShareModal';
import { BackIcon, CloseIcon, CheckIcon, PlusIcon } from './icons';
import { resendInvitation } from '../services/apiService';

const GroupMembersModal = ({ group, onClose, onAddMembers, onRemoveMember, onRevokeInvite, onUpdateInviteName }) => {
  const [showContactPicker, setShowContactPicker] = useState(false);
  const [showManualEntry, setShowManualEntry] = useState(false);
  const [selectedContacts, setSelectedContacts] = useState([]);
  const [resendingId, setResendingId] = useState(null);
  const [resendSuccess, setResendSuccess] = useState(null);
  const [confirmRemove, setConfirmRemove] = useState(null); // { type: 'member'|'invite', id, name }
  const [removing, setRemoving] = useState(false);
  const [requestSent, setRequestSent] = useState(false);
  const [editingInviteId, setEditingInviteId] = useState(null);
  const [editName, setEditName] = useState('');

  const { contacts, loading, permissionDenied, hasContactAccess, method, loadContacts, pickOne } = useContacts();
  const keyboardHeight = useKeyboardHeight();

  const isOwner = group.isOwner;
  const appMembers = group.members.filter(m => m.hasApp);
  const invitedMembers = group.pendingInvites || [];
  const emailOnlyMembers = group.members.filter(m => !m.hasApp && m.email);

  // Filter out contacts already in the group (by email or phone)
  const existingEmails = new Set(
    [...group.members, ...invitedMembers].map(m => m.email?.toLowerCase()).filter(Boolean)
  );
  const existingPhones = new Set(
    group.members.map(m => m.phone?.replace(/\D/g, '')).filter(Boolean)
  );
  const availableContacts = contacts.filter(c => {
    if (c.email && existingEmails.has(c.email.toLowerCase())) return false;
    const digits = (c.phoneNormalized || c.phone || '').replace(/\D/g, '');
    if (digits && existingPhones.has(digits)) return false;
    return true;
  });

  const handleSelectContact = (contact) => {
    if (selectedContacts.some(c => c.id === contact.id)) {
      setSelectedContacts(selectedContacts.filter(c => c.id !== contact.id));
    } else {
      setSelectedContacts([...selectedContacts, contact]);
    }
  };

  const handleAddSelected = async () => {
    if (selectedContacts.length > 0 && onAddMembers) {
      const result = await onAddMembers(group.id, selectedContacts);
      if (result?.requested) {
        setRequestSent(true);
        setTimeout(() => setRequestSent(false), 3000);
      }
      setSelectedContacts([]);
      setShowContactPicker(false);
    }
  };

  const handleFromContacts = async () => {
    console.log('[GroupMembers] From Contacts tapped, method:', method);

    // Native iOS: use the system pickContact picker — no permission prompt,
    // no bulk contact load, one contact per pick. Avoids the old stuck
    // "Loading contacts..." flow entirely.
    if (method === 'native') {
      try {
        const picked = await pickOne();
        if (!picked) return; // cancelled / timed out / no info — no-op
        if (onAddMembers) {
          const addResult = await onAddMembers(group.id, [picked]);
          if (addResult?.requested) {
            setRequestSent(true);
            setTimeout(() => setRequestSent(false), 3000);
          }
        }
      } catch (err) {
        console.warn('[GroupMembers] Native picker failed, falling back to manual:', err?.message);
        setShowManualEntry(true);
      }
      return;
    }

    if (method === 'web-picker') {
      try {
        const result = await loadContacts();
        if (result.length > 0 && onAddMembers) {
          const addResult = await onAddMembers(group.id, result);
          if (addResult?.requested) {
            setRequestSent(true);
            setTimeout(() => setRequestSent(false), 3000);
          }
        }
      } catch (err) {
        console.warn('[GroupMembers] Web picker failed, showing manual entry:', err.message);
        setShowManualEntry(true);
      }
      return;
    }

    // Manual fallback
    setShowManualEntry(true);
  };

  const handleManualAdd = async (contact) => {
    if (onAddMembers) {
      const result = await onAddMembers(group.id, [contact]);
      if (result?.requested) {
        setRequestSent(true);
        setTimeout(() => setRequestSent(false), 3000);
      }
    }
    setShowManualEntry(false);
  };

  const handleConfirmRemove = async () => {
    if (!confirmRemove) return;
    setRemoving(true);
    try {
      if (confirmRemove.type === 'member') {
        await onRemoveMember(group.id, confirmRemove.id);
      } else {
        await onRevokeInvite(group.id, confirmRemove.id);
      }
    } catch (err) {
      console.warn('[GroupMembers] Remove failed:', err.message);
    } finally {
      setRemoving(false);
      setConfirmRemove(null);
    }
  };

  // ── Confirmation dialog ──────────────────────────────────────────
  const ConfirmDialog = () => {
    if (!confirmRemove) return null;
    const label = confirmRemove.type === 'member' ? 'Remove' : 'Revoke Invitation';
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[60]">
        <div className="bg-white rounded-2xl mx-6 p-6 max-w-sm w-full shadow-xl">
          <h3 className="font-semibold text-lg text-slate-900 mb-2">{label}?</h3>
          <p className="text-sm text-slate-600 mb-5">
            {confirmRemove.type === 'member'
              ? `${confirmRemove.name} will no longer have access to shared visit summaries in this group.`
              : `The invitation for ${confirmRemove.name} will be cancelled.`}
          </p>
          <div className="flex gap-3">
            <button
              onClick={() => setConfirmRemove(null)}
              disabled={removing}
              className="flex-1 py-3 bg-slate-100 text-slate-700 rounded-xl font-medium active:bg-slate-200"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmRemove}
              disabled={removing}
              className="flex-1 py-3 bg-red-500 text-white rounded-xl font-medium active:bg-red-600 disabled:opacity-50"
            >
              {removing ? 'Removing...' : label}
            </button>
          </div>
        </div>
      </div>
    );
  };

  if (showContactPicker) {
    return (
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end z-50"
        style={{ bottom: keyboardHeight > 0 ? `${keyboardHeight}px` : 0 }}
      >
        <div className="bg-white rounded-t-3xl w-full max-h-[85vh] flex flex-col">
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between mb-1">
              <button
                onClick={() => { setShowContactPicker(false); setSelectedContacts([]); }}
                className="p-2 -ml-2 active:bg-slate-100 rounded-full text-slate-500"
              >
                <BackIcon />
              </button>
              <h3 className="font-semibold text-lg text-slate-900">Add to {group.name}</h3>
              <div className="w-10" />
            </div>
            {!isOwner && (
              <p className="text-xs text-amber-600 mt-1">Your request will need approval from the group owner</p>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {loading && (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-3 border-teal-500 border-t-transparent rounded-full animate-spin" />
                <span className="ml-3 text-slate-500 text-sm">Loading contacts...</span>
              </div>
            )}

            {permissionDenied && !loading && (
              <div className="text-center py-8 px-4">
                <p className="text-slate-700 font-medium mb-1">Contact access denied</p>
                <p className="text-sm text-slate-500 mb-4">Open Settings and allow ClariMe to access your contacts, or enter details manually.</p>
                <button
                  onClick={() => { setShowContactPicker(false); setShowManualEntry(true); }}
                  className="px-6 py-3 bg-teal-50 border-2 border-teal-300 rounded-xl text-teal-700 font-semibold active:bg-teal-100"
                >
                  Enter Manually
                </button>
              </div>
            )}

            {!loading && !permissionDenied && availableContacts.length === 0 && (
              <div className="text-center py-8">
                <p className="text-slate-500 mb-4">
                  {contacts.length === 0 ? 'No contacts found on your device' : 'All your contacts are already in this group'}
                </p>
                <button
                  onClick={() => { setShowContactPicker(false); setShowManualEntry(true); }}
                  className="px-6 py-3 bg-teal-50 border-2 border-teal-300 rounded-xl text-teal-700 font-semibold active:bg-teal-100"
                >
                  Enter Manually
                </button>
              </div>
            )}

            {!loading && !permissionDenied && availableContacts.length > 0 && (
              <div className="space-y-2">
                {availableContacts.map(contact => (
                  <button
                    key={contact.id}
                    onClick={() => handleSelectContact(contact)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                      selectedContacts.some(c => c.id === contact.id)
                        ? 'bg-teal-50 border-2 border-teal-300'
                        : 'bg-slate-50 border-2 border-transparent active:bg-slate-100'
                    }`}
                  >
                    <div className="w-12 h-12 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center text-xl font-medium text-slate-600">
                      {contact.avatar}
                    </div>
                    <div className="flex-1 text-left min-w-0">
                      <p className="font-medium text-slate-900">{contact.name}</p>
                      <p className="text-sm text-slate-500 truncate">{contact.email || contact.phone}</p>
                    </div>
                    {selectedContacts.some(c => c.id === contact.id) && (
                      <div className="w-6 h-6 bg-teal-500 rounded-full flex items-center justify-center text-white">
                        <CheckIcon />
                      </div>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {selectedContacts.length > 0 && (
            <div className="p-4 border-t border-slate-100 bg-slate-50">
              <button
                onClick={handleAddSelected}
                className="w-full py-3 bg-gradient-to-r from-teal-500 to-cyan-500 text-white rounded-xl font-semibold shadow-lg shadow-teal-500/20"
              >
                {isOwner
                  ? `Add ${selectedContacts.length} ${selectedContacts.length === 1 ? 'Person' : 'People'}`
                  : `Request to Add ${selectedContacts.length} ${selectedContacts.length === 1 ? 'Person' : 'People'}`}
              </button>
            </div>
          )}
        </div>

        {showManualEntry && (
          <ManualEntryForm
            onAdd={handleManualAdd}
            onClose={() => setShowManualEntry(false)}
          />
        )}
      </div>
    );
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-end z-50"
        style={{ bottom: keyboardHeight > 0 ? `${keyboardHeight}px` : 0 }}
      >
        <div className="bg-white rounded-t-3xl w-full max-h-[70vh] flex flex-col">
          <div className="p-4 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-lg text-slate-900">Care Group Members</h3>
              <button onClick={onClose} className="p-2 active:bg-slate-100 rounded-full text-slate-500">
                <CloseIcon />
              </button>
            </div>
            <p className="text-sm text-slate-500 mt-1">{group.name}</p>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {/* Request sent feedback */}
            {requestSent && (
              <div className="mb-3 px-4 py-2.5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-700 text-center">
                Request sent for owner approval
              </div>
            )}

            <div className="space-y-3">
              {group.members.map(member => (
                <div
                  key={member.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-slate-100 to-slate-200 rounded-full flex items-center justify-center text-xl">
                    {member.avatar}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium text-slate-900">{member.isCurrentUser ? 'Me' : member.name}</p>
                      {member.isCurrentUser && (
                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">You</span>
                      )}
                      {member.isOwner && !member.isCurrentUser && (
                        <span className="text-xs bg-teal-100 text-teal-700 px-2 py-0.5 rounded-full">Owner</span>
                      )}
                    </div>
                    <p className="text-sm text-slate-500 truncate">{member.email || member.role}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${member.hasApp ? 'bg-teal-100 text-teal-700' : 'bg-blue-100 text-blue-700'}`}>
                      {member.hasApp ? 'On ClariMe' : 'Email'}
                    </div>
                    {isOwner && !member.isOwner && onRemoveMember && (
                      <button
                        onClick={() => setConfirmRemove({ type: 'member', id: member.id, name: member.name })}
                        className="w-8 h-8 flex items-center justify-center rounded-full active:bg-red-50 text-slate-400 active:text-red-500"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {invitedMembers.map(invite => (
                <div
                  key={invite.id}
                  className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl"
                >
                  <div className="w-12 h-12 bg-gradient-to-br from-amber-50 to-orange-50 rounded-full flex items-center justify-center text-xl">
                    {invite.name ? invite.name.charAt(0).toUpperCase() : '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    {editingInviteId === invite.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="text"
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && editName.trim()) {
                              onUpdateInviteName?.(group.id, invite.id, editName.trim());
                              setEditingInviteId(null);
                            }
                          }}
                          className="flex-1 px-2 py-1 text-sm border border-teal-300 rounded-lg outline-none focus:ring-2 ring-teal-500/20"
                          autoFocus
                        />
                        <button
                          onClick={() => {
                            if (editName.trim()) {
                              onUpdateInviteName?.(group.id, invite.id, editName.trim());
                            }
                            setEditingInviteId(null);
                          }}
                          className="p-1 text-teal-600 active:text-teal-700"
                        >
                          <CheckIcon />
                        </button>
                        <button
                          onClick={() => setEditingInviteId(null)}
                          className="p-1 text-slate-400 active:text-slate-600"
                        >
                          <CloseIcon />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <p className="font-medium text-slate-900">{invite.name || 'Invited'}</p>
                        {isOwner && onUpdateInviteName && (
                          <button
                            onClick={() => { setEditingInviteId(invite.id); setEditName(invite.name || ''); }}
                            className="p-1 text-slate-400 active:text-teal-600"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                          </button>
                        )}
                      </div>
                    )}
                    <p className="text-sm text-slate-500 truncate">{invite.email}</p>
                    {invite.emailStatus === 'bounced' && (
                      <p className="text-xs text-red-600 mt-0.5">
                        This email couldn't be delivered. Check the address, fix it, and resend.
                      </p>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <div className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                      invite.emailStatus === 'bounced'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-amber-100 text-amber-700'
                    }`}>
                      {invite.emailStatus === 'bounced' ? 'Email bounced' : 'Invited'}
                    </div>
                    {invite.email && (
                      <button
                        onClick={async (e) => {
                          e.stopPropagation();
                          setResendingId(invite.id);
                          setResendSuccess(null);
                          try {
                            await resendInvitation(group.id, invite.id);
                            setResendSuccess(invite.id);
                            setTimeout(() => setResendSuccess(null), 3000);
                          } catch (err) {
                            console.warn('[ClariMe] Failed to resend invitation:', err.message);
                          } finally {
                            setResendingId(null);
                          }
                        }}
                        disabled={resendingId === invite.id}
                        className="text-xs font-medium text-teal-600 active:text-teal-700 disabled:text-slate-400"
                      >
                        {resendSuccess === invite.id ? 'Sent!' : resendingId === invite.id ? 'Sending...' : 'Resend'}
                      </button>
                    )}
                    {isOwner && onRevokeInvite && (
                      <button
                        onClick={() => setConfirmRemove({ type: 'invite', id: invite.id, name: invite.name || invite.email || 'this person' })}
                        className="w-8 h-8 flex items-center justify-center rounded-full active:bg-red-50 text-slate-400 active:text-red-500"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4 flex gap-3">
              {hasContactAccess && (
                <button
                  onClick={handleFromContacts}
                  className="flex-1 py-3.5 bg-teal-50 border-2 border-teal-300 rounded-xl text-teal-700 font-semibold active:bg-teal-100 transition-all flex items-center justify-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  From Contacts
                </button>
              )}
              <button
                onClick={() => setShowManualEntry(true)}
                className="flex-1 py-3.5 bg-teal-50 border-2 border-teal-300 rounded-xl text-teal-700 font-semibold active:bg-teal-100 transition-all flex items-center justify-center gap-2"
              >
                <PlusIcon />
                Enter Manually
              </button>
            </div>

            <div className="mt-6 pt-4 border-t border-slate-100">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-500">On ClariMe</span>
                <span className="font-medium text-slate-700">{appMembers.length}</span>
              </div>
              {invitedMembers.length > 0 && (
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-slate-500">Invited</span>
                  <span className="font-medium text-slate-700">{invitedMembers.length}</span>
                </div>
              )}
              {emailOnlyMembers.length > 0 && (
                <div className="flex items-center justify-between text-sm mt-2">
                  <span className="text-slate-500">Email only</span>
                  <span className="font-medium text-slate-700">{emailOnlyMembers.length}</span>
                </div>
              )}
            </div>
          </div>

          <div className="p-4 border-t border-slate-100 bg-slate-50">
            <button
              onClick={onClose}
              className="w-full py-3 bg-slate-200 text-slate-700 rounded-xl font-medium active:bg-slate-300 transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog />

      {showManualEntry && (
        <ManualEntryForm
          onAdd={handleManualAdd}
          onClose={() => setShowManualEntry(false)}
        />
      )}
    </>
  );
};

export default GroupMembersModal;
